import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Alert from '@amzn/meridian/alert';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Card from '@amzn/meridian/card';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import getDateInUTC from 'src/components/agile/utils/GetDateUtils';
import { AgileMacroplanogram } from 'src/components/agile/types/AgileMacroplanogramTypes';
import AgileUploadRow from 'src/components/agile/types/AgileUploadRow';
import {
  AgileRecommendationInput,
  AgileRecommendationTypes,
} from 'src/components/agile/types/AgileRecommendationTypes';
import { ErrorType, SwapAssortmentInput } from 'src/graphql/generated/graphql';
import AgileURLParameterTypes from 'src/components/agile/types/AgileURLParameterTypes';
import { useNavigate } from 'react-router-dom';
import warning from '@amzn/meridian-tokens/base/icon/alert-warning-small';
import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import plusKnockoutTokens from '@amzn/meridian-tokens/base/icon/plus-knockout';
import minusKnockoutTokens from '@amzn/meridian-tokens/base/icon/minus-knockout';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import { BLUE_COLOR, ERROR_COLOR, SUCCESS_COLOR, WARNING_COLOR } from 'src/constants/values';
import Icon from '@amzn/meridian/icon';
import Tooltip from '@amzn/meridian/tooltip';
import { getActionPublisherForProgram, publishKatalMetric } from 'src/metrics/publishers';
import {
  METHOD_NAME_BULK_UPLOAD,
  METRIC_NAME_BATCH_SWAP_ASSORTMENT,
  METRIC_NAME_PUBLISH_DRAFT_ZONE_MAP,
  MetricType,
} from 'src/constants/metricConstants';

interface SwapAssortmentConfirmationModalProps {
  openConfirmationModal: boolean;
  setOpenConfirmationModal: (value: boolean) => void;
  openErrorModal: boolean;
  setOpenErrorModal: (value: boolean) => void;
  openSuccessModal: boolean;
  setOpenSuccessModal: (value: boolean) => void;
  openPromoteDraftSuccessModal: boolean;
  setOpenPromoteDraftSuccessModal: (value: boolean) => void;
  batchSwapInput: SwapAssortmentInput[];
  listOfMacroplanogram: AgileMacroplanogram[] | undefined;
  diffTableData: AgileUploadRow[];
  onBatchSwapAssortmentAcrossZone: () => Promise<void>;
  errorMap: {
    key: string;
    error: { errorMessage: string; errorType?: ErrorType | null };
  }[];
  macroplanogramDataMap: Record<
    string,
    {
      filterFacingUpdatesList: AgileRecommendationTypes[];
      uniqueNewAsinList: AgileRecommendationInput[];
    }
  >;
  setInputTableFlag: (value: boolean) => void;
  setRecommendationFlag: (value: boolean) => void;
  isDraft: boolean;
  onPublishDraftZone: () => Promise<void>;
  openPromoteDraftErrorModal: {
    openStatus: boolean;
    errorMessage: string | undefined;
  };
  setOpenPromoteDraftErrorModal: (value: { openStatus: boolean; errorMessage: string }) => void;
}

const SwapAssortmentConfirmationModalComponent = (props: SwapAssortmentConfirmationModalProps) => {
  const {
    openConfirmationModal,
    setOpenConfirmationModal,
    openErrorModal,
    setOpenErrorModal,
    openSuccessModal,
    setOpenSuccessModal,
    openPromoteDraftSuccessModal,
    setOpenPromoteDraftSuccessModal,
    batchSwapInput,
    listOfMacroplanogram,
    diffTableData,
    onBatchSwapAssortmentAcrossZone,
    errorMap,
    macroplanogramDataMap,
    setInputTableFlag,
    setRecommendationFlag,
    isDraft,
    onPublishDraftZone,
    openPromoteDraftErrorModal,
    setOpenPromoteDraftErrorModal,
  } = props;

  const navigate = useNavigate();
  const onCancelConfirmationButton = () => {
    setOpenConfirmationModal(false);
  };

  const onConfirmButton = async () => {
    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_BATCH_SWAP_ASSORTMENT,
      1,
      MetricType.Counter,
    );
    setOpenConfirmationModal(false);
    await onBatchSwapAssortmentAcrossZone();
  };

  const onPublishDraftButton = async () => {
    publishKatalMetric(
      getActionPublisherForProgram(METHOD_NAME_BULK_UPLOAD),
      METRIC_NAME_PUBLISH_DRAFT_ZONE_MAP,
      1,
      MetricType.Counter,
    );
    // setOpenConfirmationModal(false);
    await onPublishDraftZone();
  };

  const zoneDetails = (id: string) =>
    listOfMacroplanogram?.find((macroplanogramDetails) => macroplanogramDetails.entryId === id);

  const asinMetadata = (asin: string) => diffTableData?.find((diffTableDataRow) => diffTableDataRow.asin === asin);

  const onClose = () => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const date = batchSwapInput[0]?.swapTime ? getDateInUTC(batchSwapInput[0].swapTime) : '';
    setOpenSuccessModal(false);
    setOpenPromoteDraftSuccessModal(false);
    setOpenConfirmationModal(false);
    navigate('/zoneCreation', {
      state: {
        storeId: batchSwapInput[0].storeId,
        date,
      } as AgileURLParameterTypes,
    });
  };

  function getChangeStatusForFacings(difference: number) {
    if (difference < 0) {
      return (
        <TableCell>
          <Text className={BLUE_COLOR}>
            <Icon tokens={minusKnockoutTokens} />
          </Text>
        </TableCell>
      );
    }
    if (difference === 0) {
      return (
        <TableCell>
          <Text className={SUCCESS_COLOR}>
            <Icon tokens={copyTokens} />
          </Text>
        </TableCell>
      );
    }
    return (
      <TableCell>
        <Text className={SUCCESS_COLOR}>
          <Icon tokens={plusKnockoutTokens} />
        </Text>
      </TableCell>
    );
  }
  function getTotalFohCapacity(data: AgileRecommendationTypes) {
    const isFohCapacityChanged =
      (data.fohCapacity / data.availableFacings) * data.remainingFacings === data.remainingCapacity;

    if (isFohCapacityChanged) {
      return <Text>{data.remainingCapacity}</Text>;
    }
    return (
      <Row>
        <Tooltip position="end" title="FoH capacity per facing is changed" id="myTooltip">
          <Icon className={WARNING_COLOR} tokens={warning} />
        </Tooltip>

        <Text>{data.remainingCapacity}</Text>
      </Row>
    );
  }

  return (
    <>
      <Modal open={openConfirmationModal} scrollContainer="viewport">
        <Column alignmentHorizontal="stretch">
          {Object.entries(macroplanogramDataMap).length === 0 ? (
            <Alert size="large" type="error">
              No changes to submit
            </Alert>
          ) : (
            <>
              <Alert size="large" type="warning">
                Please review and confirm the changes.
                {isDraft &&
                  " Select 'Save ' to save as draft, or 'Promote Draft to Live' to promote the zone map to live immediately. "}
              </Alert>
              <Row spacing="400">
                {Object.entries(macroplanogramDataMap).length > 0 && (
                  <Card>
                    <Table headerRows={1} spacing="medium" showStripes>
                      <TableRow>
                        <TableCell>Zone</TableCell>
                        <TableCell>ASIN</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Change</TableCell>
                        <TableCell>Diff</TableCell>
                        <TableCell>Live Date</TableCell>
                        <TableCell>UPHL2</TableCell>
                        <TableCell>#New Total Facings</TableCell>
                        <TableCell>New Total FOH Capacity</TableCell>
                      </TableRow>
                      {/* eslint-disable-next-line max-len */}
                      {Object.entries(macroplanogramDataMap).flatMap(
                        ([macroId, { filterFacingUpdatesList, uniqueNewAsinList }]) => [
                          ...uniqueNewAsinList.map((data) => (
                            <TableRow>
                              <TableCell>
                                <Text>{zoneDetails(macroId)?.macroplanogramName}</Text>
                              </TableCell>
                              <TableCell>
                                <Text>{data.asin}</Text>
                              </TableCell>
                              <TableCell>
                                <Text>{asinMetadata(data.asin)?.title?.toLocaleLowerCase() ?? 'unknown'}</Text>
                              </TableCell>
                              <TableCell>
                                <Text className={SUCCESS_COLOR}>
                                  <Icon tokens={plusKnockoutTokens} />
                                </Text>
                              </TableCell>
                              <TableCell>
                                <Text>{data.facingUnits}</Text>
                              </TableCell>
                              <TableCell>
                                <Text>
                                  {/* eslint-disable-next-line max-len */}
                                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
                                  {batchSwapInput[0]?.swapTime ? getDateInUTC(batchSwapInput[0].swapTime) : ''}
                                </Text>
                              </TableCell>
                              <TableCell>{asinMetadata(data.asin)?.uphl2?.toString().toLocaleLowerCase()}</TableCell>
                              <TableCell>NewAsin</TableCell>
                              <TableCell>
                                <Text>{data.fohCapacity}</Text>
                              </TableCell>
                            </TableRow>
                          )),
                          ...filterFacingUpdatesList.map((data) => (
                            <TableRow>
                              <TableCell>
                                <Text>{zoneDetails(macroId)?.macroplanogramName}</Text>
                              </TableCell>
                              <TableCell>{data.asin}</TableCell>
                              <TableCell>{data.title.toLocaleLowerCase()}</TableCell>
                              {+data.remainingFacings === 0 ? (
                                <TableCell>
                                  <Text className={ERROR_COLOR}>
                                    <Icon tokens={closeKnockoutTokens} />
                                  </Text>
                                </TableCell>
                              ) : (
                                getChangeStatusForFacings(data.difference)
                              )}
                              <TableCell>{data.difference}</TableCell>
                              <TableCell>
                                {/* eslint-disable-next-line max-len */}
                                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
                                <Text>
                                  {batchSwapInput[0]?.swapTime ? getDateInUTC(batchSwapInput[0].swapTime) : ''}
                                </Text>
                              </TableCell>
                              <TableCell>{data.uphl2.toLocaleLowerCase()}</TableCell>
                              <TableCell>
                                <Text>{data.remainingFacings}</Text>
                              </TableCell>
                              <TableCell>{getTotalFohCapacity(data)}</TableCell>
                            </TableRow>
                          )),
                        ],
                      )}
                    </Table>
                  </Card>
                )}
              </Row>
            </>
          )}
        </Column>
        <ModalFooter>
          <Row alignmentHorizontal="end" widths="fit">
            <Button type="secondary" size="small" onClick={onCancelConfirmationButton}>
              Cancel
            </Button>
            {Object.entries(macroplanogramDataMap).length !== 0 && (
              <Button type="primary" size="small" onClick={onConfirmButton}>
                Save
              </Button>
            )}
            {isDraft && Object.entries(macroplanogramDataMap).length !== 0 && (
              <Button type="primary" size="small" onClick={onPublishDraftButton}>
                Promote Draft to Live
              </Button>
            )}
          </Row>
        </ModalFooter>
      </Modal>
      {openErrorModal && (
        <Modal
          open={openErrorModal}
          scrollContainer="viewport"
          onClose={() => {
            setOpenErrorModal(false);
            setOpenConfirmationModal(false);
            setInputTableFlag(false);
            setRecommendationFlag(false);
          }}
        >
          <Column alignmentHorizontal="stretch">
            <Alert size="medium" type="error">
              <h3>Error Occurred!</h3>
              The following zones encountered errors during the swap operation. The remaining entries were successfully
              swapped.
            </Alert>
            <Row spacing="400">
              <Card>
                <Table headerRows={1} spacing="small">
                  <TableRow>
                    <TableCell>Zone</TableCell>
                    <TableCell>Error Message</TableCell>
                  </TableRow>
                  {errorMap.map((error) => (
                    <TableRow key={error.key}>
                      <TableCell>
                        <Text>{zoneDetails(error.key)?.macroplanogramName}</Text>
                      </TableCell>
                      <TableCell>
                        <Text>{error.error.errorMessage}</Text>
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              </Card>
            </Row>
          </Column>
        </Modal>
      )}
      {openSuccessModal && (
        <Modal
          open={openSuccessModal}
          scrollContainer="viewport"
          aria-describedby="modal-description"
          onClose={onClose}
        >
          {isDraft ? (
            <Alert type="success" size="xlarge">
              ASINs Swapped Successfully and Saved as Draft
            </Alert>
          ) : (
            <Alert type="success" size="xlarge">
              ASINs Swapped Successfully
            </Alert>
          )}
        </Modal>
      )}
      {openPromoteDraftSuccessModal && (
        <Modal
          open={openPromoteDraftSuccessModal}
          scrollContainer="viewport"
          aria-describedby="modal-description"
          onClose={onClose}
        >
          <Alert type="success" size="xlarge">
            Draft zone map promoted to live and ASINs swapped successfully
          </Alert>
        </Modal>
      )}
      {openPromoteDraftErrorModal.openStatus && (
        <Modal
          open={openPromoteDraftErrorModal.openStatus}
          scrollContainer="viewport"
          aria-describedby="modal-description"
          onClose={() => {
            setOpenPromoteDraftErrorModal({
              openStatus: false,
              errorMessage: '',
            });
            setOpenConfirmationModal(false);
            setInputTableFlag(false);
            setRecommendationFlag(false);
          }}
        >
          <Alert size="medium" type="error">
            <h3>Error Occurred!</h3>
            Error occured during promoting draft zone.
            <br />
            Error message: {openPromoteDraftErrorModal.errorMessage}
          </Alert>
        </Modal>
      )}
    </>
  );
};

export default SwapAssortmentConfirmationModalComponent;
